import { useState, useEffect } from 'react';
import axiosInstance from '@/api';

/**
 * Hook to load images from API endpoints that return files with Content-Disposition: attachment
 * Converts the image to a blob URL for display in img tags
 * Handles redirects to cddisk domain
 */
export const useImageUrl = (url: string | null | undefined): string | null => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    // If no URL or it's a data URL or blob URL, return as is
    if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
      setBlobUrl(url || null);
      return;
    }

    // If it's a local asset (not API), return as is
    if (!url.includes('/api/')) {
      setBlobUrl(url);
      return;
    }

    let objectUrl: string | null = null;
    let isCancelled = false;

    const loadImage = async () => {
      try {
        // Extract the path from the URL (remove domain if present)
        let urlPath = url.includes('://') ? new URL(url).pathname + new URL(url).search : url;

        // Remove /api prefix if present (axiosInstance already has it in baseURL)
        if (urlPath.startsWith('/api/')) {
          urlPath = urlPath.substring(4); // Remove '/api'
        }

        // Use axiosInstance which has auth token interceptor
        const response = await axiosInstance.get(urlPath, {
          responseType: 'blob'
        });

        if (isCancelled) return;

        // Check if response is actually an image by reading first few bytes
        const blobText = await response.data.slice(0, 500).text();
        const isSVG = blobText.trim().startsWith('<svg');
        const isImage =
          response.data.type.startsWith('image/') ||
          isSVG ||
          response.data.type === 'application/octet-stream';

        if (!isImage) {
          setBlobUrl(null);
          return;
        }

        // For SVG with wrong content-type, create a proper blob
        let finalBlob = response.data;
        if (isSVG && response.data.type !== 'image/svg+xml') {
          const fullText = await response.data.text();
          finalBlob = new Blob([fullText], { type: 'image/svg+xml' });
        }

        // Create blob URL from response
        objectUrl = URL.createObjectURL(finalBlob);
        setBlobUrl(objectUrl);
      } catch (error) {
        setBlobUrl(null);
      }
    };

    loadImage();

    // Cleanup: revoke blob URL when component unmounts or URL changes
    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  return blobUrl;
};
